import { useState, useEffect, useRef } from 'react';
import { fetchEventSource, EventStreamContentType } from '@microsoft/fetch-event-source';
import { message } from 'antd';
import { MessageRole } from '@/types/XAiMessage';
import { safeJsonParse } from '@/utils/index';
import { API_SUCCESS_CODE } from '@/constants/index';
import { request } from '@/utils/request';

interface IResponseData {
  code: string;
  httpCode?: number;
  data: string | { message: string };
  message: string;
  content: string;
  requestId: string;
  sessionId: string;
  error?: boolean;
  success?: boolean;
}
interface UseEventStreamProps {
  streaming: boolean;
  options?: RequestInit;
  onOpen?: (response: Response) => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
  textSpeed?: number;
}

interface UseEventStreamReturn {
  responseData: IResponseData | Record<any, any>;
  loading: boolean;
  start: (url: string, values?: any, headers?: Record<string, string>) => void;
  stop: () => void;
  onError?: (error: Error) => void;
}

export const useEventStreamRequest = ({
  onOpen = () => {},
  onError = () => {},
  streaming = true,
  onClose = () => {},
  textSpeed = 60,
}: UseEventStreamProps): UseEventStreamReturn => {
  // 使用 useRef 作为临时缓存区，降低 setState 频率
  const contentCacheRef = useRef<IResponseData[]>([]); // 存放当前完整文本
  const streamStatusRef = useRef<number>(0); // 流式接收状态 0: 未开始 1: 进行中 2: 结束
  const [loading, setLoading] = useState<boolean>(false);
  const loadingRef = useRef(loading);
  const [responseData, setResponseData] = useState<IResponseData | Record<any, any>>({ content: '' });
  const controllerRef = useRef<AbortController | null>(null);

  // 停止接收请求
  const stop = () => {
    controllerRef.current?.abort();
    contentCacheRef.current = []; // 立即清空缓存区
    setLoading(false);
  };

  const start = async (url: string, values?: any, headers = {}) => {
    const prefixUrl = url;
    setResponseData({});
    setLoading(true);
    controllerRef.current = new AbortController();
    const params = {
      ...values,
    };

    const { signal } = controllerRef.current;

    if (streaming) {
      fetchEventSource(prefixUrl, {
        method: 'POST',
        openWhenHidden: true,
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          stream: 'true',
          ...headers,
        },
        signal,
        body: JSON.stringify(params),
        async onopen(response) {
          streamStatusRef.current = 1;
          if (response.ok && response.headers.get('content-type') === EventStreamContentType) {
            onOpen?.(response);
          } else {
            if (response.status === 401) {
              message.error('用户未登录，即将前往登录……');
              // todo 需要修改
              return;
            } if (response.status === 500) {
              message.error('服务器错误，请稍后重试。'); // 使用Antd的message组件给出提示
            } else if (response.status > 500) {
              message.error('网络不给力，请稍后重试。');
            }
            if (response.status >= 500) {
              setResponseData((prevResponseData: IResponseData) => {
                return {
                  ...prevResponseData,
                  content: '网络不给力，请稍后重试~~~~~',
                  requestId: '',
                  role: MessageRole.assistant,
                  httpCode: response.status,
                  error: true,
                  code: '',
                };
              });
            }
            onError?.(new Error(`Unexpected response status: ${response.status}`));
          }
        },
        onmessage: (event: { data: string }) => {
          // 解析事件数据
          const eventData = safeJsonParse(event.data, {});
          // 空消息处理
          if (!eventData?.data && !eventData?.content) return;
          // 如果请求失败，则停止请求
          if (eventData?.result?.code !== API_SUCCESS_CODE) {
            setResponseData({
              ...eventData,
              code: eventData.code,
              content: eventData.message || '网络不给力，请稍后重试~~',
              requestId: eventData.requestId,
              role: MessageRole.assistant,
              httpCode: 200,
              error: true,
            });
            stop();
            return;
          }
          // 推送内容到缓存区，后续通过Interval定时器，将缓存区内容设置到响应数据中
          contentCacheRef.current?.push(eventData);
        },
        onerror: (error: Error) => {
          streamStatusRef.current = 2;
          setLoading(false);
          onError?.(error);
        },
        onclose: () => {
          streamStatusRef.current = 2;
          onClose();
        },
      });
    } else {
      try {
        const response = await request.post(url, params);
        setResponseData(response);
        setLoading(false);
      } catch (error: any) {
        onError?.(error);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // 初始化定时器 将缓存区有新内容时设置到响应数据中
  useEffect(() => {
    const timer = setInterval(() => {
      if (contentCacheRef.current?.length > 0) {
        // 如果缓存区有新内容，则设置到响应数据中
        const eventData = contentCacheRef.current?.shift();
        const newData = eventData?.data;
        setResponseData((prevResponseData: IResponseData) => {
          const prevContent = prevResponseData.content ?? '';
          return {
            ...eventData,
            code: eventData?.code,
            content: `${prevContent}${newData}`,
            requestId: eventData?.requestId,
            role: MessageRole.assistant,
          };
        });
      } else if (streamStatusRef.current === 2 && loadingRef.current) {
        streamStatusRef.current = 0;
        setLoading(false);
      }
    }, textSpeed); // 渲染间隔

    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      stop();
    };
  }, []);

  return {
    responseData,
    loading,
    start,
    stop,
    onError,
  };
};

export default useEventStreamRequest;
